import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Alert, Breakdown, LoadingBlock, StatCard } from '../components/ui';
import { formatMoney, formatSwimTimeShort, formatDateShort } from '../lib/format';
import type { AdminEvent, EventStats } from '../lib/types';

export function DashboardPage() {
  const stats = useAsync<EventStats>((signal) => api.admin.stats(signal), []);
  const event = useAsync<AdminEvent>((signal) => api.admin.getEvent(signal), []);

  if (stats.loading || event.loading) return <LoadingBlock message="Loading dashboard..." />;

  if (stats.error || event.error || !stats.data || !event.data) {
    return (
      <Alert variant="error" title="Dashboard unavailable">
        <p>{stats.error ?? event.error ?? 'Please try again.'}</p>
        <button type="button" className="btn btn-secondary btn-sm" onClick={stats.reload}>
          Retry
        </button>
      </Alert>
    );
  }

  const s = stats.data;
  const e = event.data;
  const currency = e.currency;
  const fillFraction = s.capacity > 0 ? s.active / s.capacity : 0;
  const unpaidCount = s.byPaymentStatus['unpaid'] ?? 0;
  const pendingCount = s.byStatus['pending'] ?? 0;
  const recentDays = s.registrationsByDay.slice(-14);
  const peakDay = recentDays.reduce((max, d) => Math.max(max, d.count), 0);

  const categoryLabels = new Map(e.categories.map((c) => [c.id, c.label]));

  return (
    <div className="stack">
      <div className="page-header">
        <div>
          <h1>{e.name}</h1>
          <p>
            {formatDateShort(e.eventDate)} &middot; {e.venue} &middot;{' '}
            {e.distanceMeters.toLocaleString('en-GB')} m
          </p>
        </div>
        <div className="row">
          <Link to="/admin/entries" className="btn btn-secondary">
            Manage entries
          </Link>
          <Link to="/admin/heats" className="btn btn-primary">
            {e.seedingMode === 'waves' ? 'Waves' : 'Heats'}
          </Link>
        </div>
      </div>

      {e.heatsGeneratedAt && !e.heatsPublishedAt && (
        <Alert variant="warning" title="Start lists are not published">
          <p style={{ marginBottom: 0 }}>
            {e.seedingMode === 'waves' ? 'Waves' : 'Heats'} have been generated but competitors
            cannot see them yet. <Link to="/admin/heats">Review and publish</Link>.
          </p>
        </Alert>
      )}

      {unpaidCount > 0 && (
        <Alert variant="info" title={`${unpaidCount} unpaid ${unpaidCount === 1 ? 'entry' : 'entries'}`}>
          <p style={{ marginBottom: 0 }}>
            {formatMoney(s.revenuePence.outstanding, currency)} outstanding.{' '}
            <Link to="/admin/entries?paymentStatus=unpaid">Review unpaid entries</Link>.
          </p>
        </Alert>
      )}

      <div className="grid grid-4">
        <StatCard
          label="Entries taken"
          value={s.active.toLocaleString('en-GB')}
          sub={`of ${s.capacity.toLocaleString('en-GB')} places`}
          meter={fillFraction}
        />
        <StatCard
          label="Places remaining"
          value={s.placesRemaining.toLocaleString('en-GB')}
          sub={s.placesRemaining === 0 ? 'Event is full' : undefined}
        />
        <StatCard
          label="Awaiting confirmation"
          value={pendingCount.toLocaleString('en-GB')}
          sub="Status: pending"
        />
        <StatCard
          label="Fees collected"
          value={formatMoney(s.revenuePence.collected, currency)}
          sub={`${formatMoney(s.revenuePence.outstanding, currency)} outstanding`}
        />
      </div>

      <div className="grid grid-3">
        <StatCard
          label="Fastest predicted"
          value={s.seedTime.fastestMs === null ? '-' : formatSwimTimeShort(s.seedTime.fastestMs)}
        />
        <StatCard
          label="Median predicted"
          value={s.seedTime.medianMs === null ? '-' : formatSwimTimeShort(s.seedTime.medianMs)}
        />
        <StatCard
          label="Slowest predicted"
          value={s.seedTime.slowestMs === null ? '-' : formatSwimTimeShort(s.seedTime.slowestMs)}
        />
      </div>

      <div className="grid grid-2">
        <Breakdown title="Entry status" data={s.byStatus} />
        <Breakdown title="Payment status" data={s.byPaymentStatus} />
        <Breakdown
          title="Category"
          data={s.byCategory}
          formatLabel={(id) => categoryLabels.get(id) ?? id}
        />
        <Breakdown title="Age band on event day" data={s.byAgeGroup} formatLabel={(k) => k} />
      </div>

      <section className="card card-tight">
        <h3>Entries received (last 14 days with activity)</h3>
        {recentDays.length === 0 ? (
          <p className="faint" style={{ marginBottom: 0 }}>
            No entries yet.
          </p>
        ) : (
          <table>
            <caption className="visually-hidden">Entries received per day</caption>
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Entries</th>
                <th scope="col">
                  <span className="visually-hidden">Relative volume</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {recentDays.map((day) => (
                <tr key={day.date}>
                  <td className="nowrap">{formatDateShort(day.date)}</td>
                  <td className="mono">{day.count}</td>
                  <td style={{ width: '60%' }}>
                    <span className="breakdown-bar" style={{ display: 'block' }}>
                      <span
                        style={{ width: peakDay > 0 ? `${(day.count / peakDay) * 100}%` : '0%' }}
                      />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
